import React, { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { UpdateStageForm } from '@asd-stan/standard/domain/stage-update.entity';
import { StandardStage } from '@asd-stan/standard/domain/standard-stage.entity';
import { Checkbox } from '@components/checkbox/checkbox';
import { FormInput } from '@components/form-input/form-input';
import { Field, Formik, useFormikContext } from 'formik';

import {
	StageApprovalFormStyled,
	StageItem,
	StagePickerContainer,
	StageSelectorTitle,
} from '@asd-stan/standard/components/single-standard/stage-update/add-new-stage/add-new-stage.styled';

interface Props {
	newStageOptions: StandardStage[];
	priceAdded: boolean;
	fileAdded: boolean;
}

export const AddNewStageEA: FC<Props> = ({ newStageOptions, priceAdded, fileAdded }) => {
	const { t } = useTranslation();
	const { values, setFieldValue } = useFormikContext<UpdateStageForm>();

	const options = newStageOptions.map(({ code, name, immediatelyPublication }) => {
		const active = values.stages.newStages.includes(code);
		const disabledMessage =
			immediatelyPublication && !priceAdded
				? t('standard.singleStandard.stageUpdateModal.priceNotAdded')
				: immediatelyPublication && !fileAdded
				  ? t('standard.singleStandard.stageUpdateModal.fileNotAdded')
				  : null;

		const handleChange = () => {
			if (disabledMessage) {
				return;
			}
			if (active) {
				setFieldValue(
					'stages.newStages',
					values.stages.newStages.filter(c => code !== c)
				);
				return;
			}
			setFieldValue('stages.newStages', [...values.stages.newStages, code]);
		};

		return {
			code,
			name,
			active,
			disabledMessage,
			handleChange,
		};
	});

	return (
		<>
			<StageSelectorTitle>
				{t('standard.singleStandard.stageUpdateModal.allStages')}
			</StageSelectorTitle>
			<StagePickerContainer>
				{options.map(({ code, name, active, disabledMessage, handleChange }) => {
					const inArray = values.stages.newStages.includes(code);
					return (
						<StageItem
							onClick={handleChange}
							className={[
								active && 'active',
								disabledMessage && 'disabled',
								values.stages.newStages.length > 0 && !inArray && 'hidden',
							]
								.filter(Boolean)
								.join(' ')}
							key={code}>
							<Checkbox
								checked={active}
								onChange={handleChange}
								label={
									<div>
										<div className="optionName">
											{code} {name}
										</div>
										{disabledMessage && (
											<div className="disabledOptionMessage">{disabledMessage}</div>
										)}
									</div>
								}
							/>
						</StageItem>
					);
				})}
			</StagePickerContainer>
			<ApprovelStageEa />
		</>
	);
};

const normalizeReasonReview = (review: unknown): string[] => {
	if (Array.isArray(review)) {
		return review.filter(
			(item): item is string => typeof item === 'string' && item.trim().length > 0
		);
	}

	if (typeof review === 'string' && review.trim().length > 0) {
		return [review];
	}

	return [];
};

const ReasonReviewCheckboxes = ({
	title,
	options,
}: {
	title: string;
	options: Array<{ value: string; label: string }>;
}) => {
	const { values, setFieldValue } = useFormikContext<{ review: string[]; comment: string }>();
	const selectedReviews = normalizeReasonReview(values.review);

	const toggleReviewValue = (value: string) => {
		if (selectedReviews.includes(value)) {
			setFieldValue(
				'review',
				selectedReviews.filter(item => item !== value)
			);
			return;
		}

		setFieldValue('review', [...selectedReviews, value]);
	};

	return (
		<div className="radioSection">
			<label title={title}>{title}</label>
			{options.map(option => (
				<div className="radio" key={option.value}>
					<Checkbox
						checked={selectedReviews.includes(option.value)}
						onChange={() => toggleReviewValue(option.value)}
						label={option.label}
					/>
				</div>
			))}
		</div>
	);
};

const ApprovelStageEa = () => {
	const parentForm = useFormikContext<UpdateStageForm>();
	const selectedStageCode = parentForm.values.stages.newStages[0];
	const currentReasonStageCode = parentForm.values.eaStageReason?.stageCode;
	const setFieldValue = parentForm.setFieldValue;

	useEffect(() => {
		if (!selectedStageCode) {
			setFieldValue('eaStageReason', null);
			return;
		}

		if (currentReasonStageCode === selectedStageCode) {
			return;
		}

		setFieldValue('eaStageReason', {
			stageCode: selectedStageCode,
			data: {},
		});
	}, [currentReasonStageCode, selectedStageCode, setFieldValue]);

	if (!parentForm.values.stages.newStages.length) return null;

	if (selectedStageCode === '00.50') return <ApprovelForm0050 />;
	if (selectedStageCode === '00.98') return <ApprovelForm0098 />;
	if (selectedStageCode === '30.99') return <ApprovelForm3099 />;
	return null;
};

const ApprovelForm0050 = () => {
	const parentForm = useFormikContext<UpdateStageForm>();
	type tmpForm = {
		review: string[];
		comment: string;
	};
	const reasonData = parentForm.values.eaStageReason?.data as Partial<tmpForm> | undefined;
	const initialValues: tmpForm = {
		review: normalizeReasonReview(reasonData?.review),
		comment: typeof reasonData?.comment === 'string' ? reasonData.comment : '',
	};

	const onValuesChange = useCallback(
		(values: tmpForm) => {
			parentForm.setFieldValue('eaStageReason.data', values);
		},
		[parentForm.setFieldValue]
	);

	return (
		<StageApprovalFormStyled>
			<Formik initialValues={initialValues} onSubmit={() => undefined} enableReinitialize>
				<ApprovelForm0050Content onValuesChange={onValuesChange} />
			</Formik>
		</StageApprovalFormStyled>
	);
};

const ApprovelForm0050Content = ({
	onValuesChange,
}: {
	onValuesChange: (values: { review: string[]; comment: string }) => void;
}) => {
	const { t } = useTranslation();
	const { values } = useFormikContext<{ review: string[]; comment: string }>();

	useEffect(() => {
		onValuesChange(values);
	}, [onValuesChange, values]);

	return (
		<div className="miniform">
			<ReasonReviewCheckboxes
				title={t('standard.singleStandard.stageUpdateModal.reasonForm.0050.field1.title')}
				options={[
					{
						value: '1',
						label: t('standard.singleStandard.stageUpdateModal.reasonForm.0050.field1.options.1'),
					},
					{
						value: '2',
						label: t('standard.singleStandard.stageUpdateModal.reasonForm.0050.field1.options.2'),
					},
					{
						value: '3',
						label: t('standard.singleStandard.stageUpdateModal.reasonForm.0050.field1.options.3'),
					},
				]}
			/>

			<Field
				className="qweasd"
				component={FormInput}
				name="comment"
				title={t('standard.singleStandard.stageUpdateModal.reasonForm.0050.field2.title')}
			/>
		</div>
	);
};

const ApprovelForm0098 = () => {
	const parentForm = useFormikContext<UpdateStageForm>();
	type tmpForm = {
		comment: string;
	};
	const reasonData = parentForm.values.eaStageReason?.data as Partial<tmpForm> | undefined;
	const initialValues: tmpForm = {
		comment: typeof reasonData?.comment === 'string' ? reasonData.comment : '',
	};

	const onValuesChange = useCallback(
		(values: tmpForm) => {
			parentForm.setFieldValue('eaStageReason.data', values);
		},
		[parentForm.setFieldValue]
	);

	return (
		<StageApprovalFormStyled>
			<Formik initialValues={initialValues} onSubmit={() => undefined} enableReinitialize>
				<ApprovelForm0098Content onValuesChange={onValuesChange} />
			</Formik>
		</StageApprovalFormStyled>
	);
};

const ApprovelForm0098Content = ({ onValuesChange }: { onValuesChange: (values: { comment: string }) => void }) => {
	const { t } = useTranslation();
	const { values } = useFormikContext<{ comment: string }>();

	useEffect(() => {
		onValuesChange(values);
	}, [onValuesChange, values]);

	return (
		<div className="miniform -mt-30">
			<Field
				component={FormInput}
				name="comment"
				title={t('standard.singleStandard.stageUpdateModal.reasonForm.0098.field1.title')}
			/>
		</div>
	);
};

const ApprovelForm3099 = () => {
	const parentForm = useFormikContext<UpdateStageForm>();
	type tmpForm = {
		review: string[];
		comment: string;
	};
	const reasonData = parentForm.values.eaStageReason?.data as Partial<tmpForm> | undefined;
	const initialValues: tmpForm = {
		review: normalizeReasonReview(reasonData?.review),
		comment: typeof reasonData?.comment === 'string' ? reasonData.comment : '',
	};

	const onValuesChange = useCallback(
		(values: tmpForm) => {
			parentForm.setFieldValue('eaStageReason.data', values);
		},
		[parentForm.setFieldValue]
	);

	return (
		<StageApprovalFormStyled>
			<Formik initialValues={initialValues} onSubmit={() => undefined} enableReinitialize>
				<ApprovelForm3099Content onValuesChange={onValuesChange} />
			</Formik>
		</StageApprovalFormStyled>
	);
};

const ApprovelForm3099Content = ({
	onValuesChange,
}: {
	onValuesChange: (values: { review: string[]; comment: string }) => void;
}) => {
	const { t } = useTranslation();
	const { values } = useFormikContext<{ review: string[]; comment: string }>();

	useEffect(() => {
		onValuesChange(values);
	}, [onValuesChange, values]);

	return (
		<div className="miniform">
			<ReasonReviewCheckboxes
				title={t('standard.singleStandard.stageUpdateModal.reasonForm.3099.field1.title')}
				options={[
					{
						value: '1',
						label: t('standard.singleStandard.stageUpdateModal.reasonForm.3099.field1.options.1'),
					},
					{
						value: '2',
						label: t('standard.singleStandard.stageUpdateModal.reasonForm.3099.field1.options.2'),
					},
				]}
			/>

			<Field
				component={FormInput}
				name="comment"
				title={t('standard.singleStandard.stageUpdateModal.reasonForm.3099.field2.title')}
			/>
		</div>
	);
};
